import clsx from "clsx";
import { AgingReport, AgingReportMessage, AgingReportMessageResponse, toMonthYear, toPrettyDate } from "common";
import React, { useState } from "react";
import { notify } from "../util/notify";
import { useUser } from "../store";
import { useConfirmation } from "./ConfirmationProvider";
import { ThreadStatus } from "../util/alerts";
import ReplyComposer from "./ReplyComposer";

/** Gives each person a steady color, so a conversation can be followed at a glance */
const avatarColor = (name: string) => {
    let hash = 0;
    for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % 360;
    return `hsl(${hash}, 45%, 42%)`;
};

export const Avatar = ({ name }: { name: string }) => (
    <span className="chat-avatar" style={{ background: avatarColor(name) }} aria-hidden="true">
        {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
);

const fullTime = (value?: string) => (value && !Number.isNaN(Date.parse(value)) ? new Date(value).toLocaleString() : undefined);

export default function ReportMessage({ message, refreshReport, parentReport, isArchived = false, status, displayNames, users, monthOptions }: { parentReport: AgingReport, message: AgingReportMessage; refreshReport: (report: AgingReport) => void; isArchived?: boolean; status?: ThreadStatus; displayNames?: Record<string, string>; users?: any[]; monthOptions?: { value: string; label: string }[] }) {
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const [editing, setEditing] = useState<{ responseId?: string } | null>(null);
    const [draftMessage, setDraftMessage] = useState("");
    const [draftDeadline, setDraftDeadline] = useState("");
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [showArchivedResponses, setShowArchivedResponses] = useState(false);
    const { invokeApi, isAdmin, user } = useUser();
    const confirm = useConfirmation();
    const activeResponses = message.responses.filter((response) => !response.archivedAt);
    const archivedResponses = message.responses.filter((response) => Boolean(response.archivedAt));
    const nameOf = (username: string) => displayNames?.[username] || username;
    const isOwn = (username: string) => Boolean(user?.username) && username === user.username;

    const composerRef = React.useRef<HTMLTextAreaElement>(null);

    const focusReply = () => {
        setCollapsed(false);
        setTimeout(() => composerRef.current?.focus(), 0);
    };

    const startEditing = (item: AgingReportMessage | AgingReportMessageResponse, responseId?: string) => {
        setEditing({ responseId });
        setDraftMessage(item.message);
        setDraftDeadline(item.deadline ?? "");
    };

    const cancelEditing = () => setEditing(null);

    const saveEdit = async () => {
        if (!editing) return;
        if (!draftMessage.trim()) {
            notify.error("Message cannot be empty.");
            return;
        }
        setIsSavingEdit(true);
        try {
            const report: AgingReport = await invokeApi({
                endpoint: "/editReportMessage",
                data: {
                    residentName: parentReport.residentName,
                    billType: parentReport.billType,
                    index: parentReport.index,
                    messageId: message.id,
                    responseId: editing.responseId,
                    message: draftMessage,
                    deadline: draftDeadline,
                },
            });
            notify.success(editing.responseId ? "Response updated." : "Message updated.");
            setEditing(null);
            refreshReport(report);
        } catch {
            // invokeApi already reports the failure
        } finally {
            setIsSavingEdit(false);
        }
    };

    const renderEditForm = () => (
        <div className="message-edit-form">
            <textarea
                aria-label="Edit message"
                value={draftMessage}
                onChange={(event) => setDraftMessage(event.target.value)}
                autoFocus
            />
            <div className="message-edit-actions">
                <label>
                    <span>Follow-up date</span>
                    <input
                        type="date"
                        value={draftDeadline}
                        onChange={(event) => setDraftDeadline(event.target.value)}
                    />
                </label>
                <div className="message-edit-buttons">
                    <button type="button" className="message-edit-cancel" onClick={cancelEditing} disabled={isSavingEdit}>
                        Cancel
                    </button>
                    <button type="button" className="message-edit-save" onClick={saveEdit} disabled={isSavingEdit}>
                        {isSavingEdit ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );

    const wasEdited = (item: AgingReportMessage | AgingReportMessageResponse) => Boolean(item.editHistory?.length);


    const deleteReportMessage = async () => {
        const confirmed = await confirm({
            title: "Archive message?",
            message: "This message and its conversation will be moved to the archive, where it can still be viewed.",
            confirmLabel: "Archive message",
        });
        if (!confirmed) return;
        invokeApi({
            endpoint: "/deleteReportMessage",
            data: {
                residentName: parentReport.residentName,
                billType: parentReport.billType,
                index: parentReport.index,
                messageId: message.id,
            },
        }).then((report: AgingReport) => {
            notify.success("Message archived.");
            refreshReport(report);
        });
    };

    const deleteResponse = async (response: AgingReportMessageResponse) => {
        const confirmed = await confirm({
            title: "Archive response?",
            message: "This response will be moved to the archive, where it can still be viewed.",
            confirmLabel: "Archive response",
        });
        if (!confirmed) return;
        invokeApi({
            endpoint: "/deleteReportMessageResponse",
            data: {
                residentName: parentReport.residentName,
                billType: parentReport.billType,
                index: parentReport.index,
                messageId: message.id,
                responseId: response.id,
            },
        }).then((report: AgingReport) => {
            notify.success("Response archived.");
            refreshReport(report);
        });
    };

    const canDeleteMessage = (username: string) => isAdmin || username === user?.username;

    return (
        <div
            key={message.id}
            className={clsx("thread", { "is-overdue": status?.overdue, "is-new": status?.isNew })}
            data-thread-id={message.id}
            data-overdue={status?.overdue || undefined}
            data-new={status?.isNew || undefined}
        >
            {(status?.overdue || status?.isNew || isArchived) && <div className="thread-heading">
                {status?.overdue && (
                    <span className="thread-flag is-overdue">
                        <i className="fa-solid fa-clock" aria-hidden="true" />
                        Overdue{status.deadline ? ` since ${toPrettyDate(status.deadline)}` : ""}
                    </span>
                )}
                {status?.isNew && (
                    <span className="thread-flag is-new">
                        <i className="fa-solid fa-envelope" aria-hidden="true" />
                        New
                    </span>
                )}
                {isArchived && (
                    // Archived threads are not under a month tab, so say which months they are about
                    <div className="month-badge">
                        <i className="fa-regular fa-calendar" aria-hidden="true" />
                        <span>{(message.months ?? []).map(toMonthYear).join(' · ') || "General"}</span>
                    </div>
                )}
                {isArchived && (
                    <span className="archived-message-label">
                        <i className="fa-solid fa-box-archive" aria-hidden="true" />
                        Archived{message.archivedBy ? ` by ${message.archivedBy}` : ''}
                    </span>
                )}
            </div>}

            <div className={clsx("message-card", { "is-own": isOwn(message.author), "is-editing": editing && !editing.responseId })}>
                <Avatar name={nameOf(message.author)} />
                <div className="message-top">
                    <div className="message-text">
                        <strong className="message-author">{nameOf(message.author)}</strong>
                        {editing && !editing.responseId ? renderEditForm() : <span>{message.message}</span>}
                    </div>
                    {!isArchived && !(editing && !editing.responseId) && <div className="message-right">
                        <button
                            className="message-action"
                            type="button"
                            title="Reply to message"
                            aria-label="Reply to message"
                            onClick={focusReply}
                        >
                            <i className="fa-solid fa-reply" aria-hidden="true" />
                        </button>
                        {canDeleteMessage(message.author) &&
                            <button
                                className="message-action"
                                type="button"
                                title="Edit message"
                                aria-label="Edit message"
                                onClick={() => startEditing(message)}
                            >
                                <i className="fa-solid fa-pen" aria-hidden="true" />
                            </button>
                        }
                        {canDeleteMessage(message.author) &&
                            <button
                                className="message-action message-action-delete"
                                type="button"
                                title="Archive message"
                                aria-label="Archive message"
                                onClick={deleteReportMessage}
                            >
                                <i className="fa-solid fa-box-archive" aria-hidden="true" />
                            </button>
                        }
                        <button
                            className="message-action"
                            type="button"
                            title={collapsed ? "Expand conversation" : "Collapse conversation"}
                            aria-label={collapsed ? "Expand conversation" : "Collapse conversation"}
                            aria-expanded={!collapsed}
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <i className={`fa-solid ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`} aria-hidden="true" />
                        </button>
                    </div>}
                </div>

                <div className="meta">
                    <span title={fullTime(message.createdAt)}><i className="fa-regular fa-clock" aria-hidden="true" /> {toPrettyDate(message.createdAt)}</span>
                    {wasEdited(message) && <span className="edited-label" title={`Last edited ${toPrettyDate(message.updatedAt)}`}>Edited</span>}
                    {message.deadline && <span className="deadline"><i className="fa-regular fa-calendar-check" aria-hidden="true" /> Due {toPrettyDate(message.deadline)}</span>}
                </div>
            </div>

            {!collapsed && (
                <div className="responses">
                    {(isArchived ? message.responses : activeResponses).map(response => (
                        <div key={response.id} className={clsx("response-card", { "is-own": isOwn(response.author), "is-editing": editing?.responseId === response.id })}>
                            <Avatar name={nameOf(response.author)} />
                            <div className="message-top">
                                <div className="message-text">
                                    <strong className="message-author">{nameOf(response.author)}</strong>
                                    {editing?.responseId === response.id ? renderEditForm() : <span>{response.message}</span>}
                                    {isArchived && response.archivedAt && (
                                        <span className="archived-message-label">
                                            <i className="fa-solid fa-box-archive" aria-hidden="true" />
                                            Archived
                                        </span>
                                    )}
                                </div>
                                {!isArchived && editing?.responseId !== response.id && <div className="message-right">
                                    <button
                                        className="message-action"
                                        type="button"
                                        title="Reply to response"
                                        aria-label={`Reply to ${response.author}`}
                                        onClick={focusReply}
                                    >
                                        <i className="fa-solid fa-reply" aria-hidden="true" />
                                    </button>
                                    {canDeleteMessage(response.author) &&
                                        <button
                                            className="message-action"
                                            type="button"
                                            title="Edit response"
                                            aria-label="Edit response"
                                            onClick={() => startEditing(response, response.id)}
                                        >
                                            <i className="fa-solid fa-pen" aria-hidden="true" />
                                        </button>
                                    }
                                    {canDeleteMessage(message.author) &&
                                        <button
                                            className="message-action message-action-delete"
                                            type="button"
                                            title="Archive response"
                                            aria-label="Archive response"
                                            onClick={() => deleteResponse(response)}
                                        >
                                            <i className="fa-solid fa-box-archive" aria-hidden="true" />
                                        </button>
                                    }
                                </div>}
                            </div>

                            <div className="meta">
                                <span className="sent" title={fullTime(response.createdAt)}><i className="fa-regular fa-clock" aria-hidden="true" /> {toPrettyDate(response.createdAt)}</span>
                                {wasEdited(response) && <span className="edited-label" title={`Last edited ${toPrettyDate(response.updatedAt)}`}>Edited</span>}
                                {response.deadline && <span className="deadline"><i className="fa-regular fa-calendar-check" aria-hidden="true" /> Due {toPrettyDate(response.deadline)}</span>}
                            </div>
                        </div>
                    ))}
                    {!isArchived && archivedResponses.length > 0 && (
                        <>
                            <button
                                type="button"
                                className="archived-responses-toggle"
                                aria-expanded={showArchivedResponses}
                                onClick={() => setShowArchivedResponses((isOpen) => !isOpen)}
                            >
                                <i className="fa-solid fa-box-archive" aria-hidden="true" />
                                {showArchivedResponses ? "Hide" : "View"} archived responses ({archivedResponses.length})
                            </button>
                            {showArchivedResponses && archivedResponses.map((response) => (
                                <div key={response.id} className="response-card is-archived-response">
                                    <div className="message-top">
                                        <div className="message-text">
                                            <strong className="message-author">{response.author}</strong>
                                            <span>{response.message}</span>
                                            <span className="archived-message-label">
                                                <i className="fa-solid fa-box-archive" aria-hidden="true" />
                                                Archived{response.archivedBy ? ` by ${response.archivedBy}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="meta">
                                        <span><i className="fa-regular fa-clock" aria-hidden="true" /> {toPrettyDate(response.createdAt)}</span>
                                        {response.deadline && <span className="deadline"><i className="fa-regular fa-calendar-check" aria-hidden="true" /> Due {toPrettyDate(response.deadline)}</span>}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                    {!isArchived && (
                        <ReplyComposer
                            ref={composerRef}
                            report={parentReport}
                            thread={message}
                            users={users}
                            monthOptions={monthOptions}
                            refreshReport={refreshReport}
                        />
                    )}
                </div>
            )
            }

        </div >
    );
}